/**
 * Salary rules shared by the API and the SalarySplitEditor (FR-SAL-2/3, ADR-011, ADR-018).
 * All amounts are whole taka.
 */
/** Largest whole-taka amount that fits `numeric(12,2)`. */
export const MAX_MONEY = 9_999_999_999;
/**
 * Cash / A/C amounts implied by the payment mode (FR-SAL-2). For `CASH_AND_BANK` the entered
 * cash is used and A/C = gross − cash; for the other modes any entered cash is ignored.
 */
export function deriveAmounts(gross, mode, cashAmount) {
    switch (mode) {
        case 'CASH':
            return { cashAmount: gross, acAmount: 0 };
        case 'BANK':
            return { cashAmount: 0, acAmount: gross };
        case 'CASH_AND_BANK': {
            const cash = cashAmount ?? 0;
            return { cashAmount: cash, acAmount: gross - cash };
        }
    }
}
/**
 * Active earning `SALARY` components in display order — the ones a salary split uses. Payroll
 * components (Bonus, LTA, …) are never part of salary history (FR-PAY-1).
 */
export function splitComponents(components) {
    return components
        .filter((c) => c.active && c.kind === 'EARNING' && c.scope === 'SALARY')
        .sort((a, b) => a.sortOrder - b.sortOrder || a.code.localeCompare(b.code));
}
/**
 * Default A/C split (FR-SAL-3): each active earning component gets `round(ac × default_pct)`
 * whole taka; the `is_remainder` component (Basic) takes `ac − Σ others`, absorbing rounding.
 * Example: 161365 → Basic 96818, House Rent 48410, Conveyance 4841, Medical 11296.
 */
export function defaultSplit(ac, components) {
    if (!Number.isInteger(ac) || ac < 0) {
        throw new RangeError('A/C amount must be a whole, non-negative taka amount');
    }
    if (ac === 0)
        return [];
    const active = splitComponents(components);
    if (active.filter((c) => c.isRemainder).length !== 1) {
        throw new Error('Exactly one active earning pay component must be the remainder component');
    }
    const others = active
        .filter((c) => !c.isRemainder)
        .reduce((sum, c) => sum + Math.round((ac * (c.defaultPct ?? 0)) / 100), 0);
    if (others > ac) {
        throw new Error('Pay component default percentages exceed 100%');
    }
    return active.map((c) => ({
        payComponentId: c.id,
        amount: c.isRemainder ? ac - others : Math.round((ac * (c.defaultPct ?? 0)) / 100),
    }));
}
const isWholeTaka = (n) => Number.isInteger(n) && n >= 0 && n <= MAX_MONEY;
/**
 * Validates a salary record against FR-SAL-2/3. Pass the pay-component catalog to also check
 * that every component is an active earning component. Returns an empty array when valid.
 */
export function validateSalary(input, catalog) {
    const issues = [];
    const { gross, paymentMode } = input;
    if (!isWholeTaka(gross) || gross === 0) {
        issues.push({
            path: ['gross'],
            code: 'gross_invalid',
            message: 'Gross salary must be a whole taka amount greater than 0',
        });
        return issues;
    }
    if (paymentMode === 'CASH_AND_BANK') {
        const cash = input.cashAmount;
        if (cash === null || cash === undefined) {
            issues.push({
                path: ['cashAmount'],
                code: 'cash_required',
                message: 'Cash amount is required for Cash + Bank A/C',
            });
            return issues;
        }
        if (!isWholeTaka(cash)) {
            issues.push({
                path: ['cashAmount'],
                code: 'cash_invalid',
                message: 'Cash amount must be a whole taka amount',
            });
            return issues;
        }
        if (cash <= 0 || cash >= gross) {
            issues.push({
                path: ['cashAmount'],
                code: 'cash_out_of_range',
                message: 'Cash amount must be more than 0 and less than gross',
            });
            return issues;
        }
    }
    const { acAmount } = deriveAmounts(gross, paymentMode, input.cashAmount);
    const components = input.components ?? [];
    if (acAmount === 0) {
        if (components.length > 0) {
            issues.push({
                path: ['components'],
                code: 'components_not_allowed',
                message: 'Cash salaries have no A/C components',
            });
        }
        return issues;
    }
    if (components.length === 0) {
        issues.push({
            path: ['components'],
            code: 'components_required',
            message: 'A/C components are required when part of the salary is paid to bank',
        });
        return issues;
    }
    const allowed = catalog ? new Set(splitComponents(catalog).map((c) => c.id)) : undefined;
    const seen = new Set();
    let total = 0;
    components.forEach((c, i) => {
        if (!isWholeTaka(c.amount)) {
            issues.push({
                path: ['components', i, 'amount'],
                code: 'component_amount_invalid',
                message: 'Component amounts must be whole taka amounts of 0 or more',
            });
        }
        else {
            total += c.amount;
        }
        if (seen.has(c.payComponentId)) {
            issues.push({
                path: ['components', i, 'payComponentId'],
                code: 'component_duplicate',
                message: 'Each pay component can appear only once',
            });
        }
        seen.add(c.payComponentId);
        if (allowed && !allowed.has(c.payComponentId)) {
            issues.push({
                path: ['components', i, 'payComponentId'],
                code: 'component_unknown',
                message: 'Not an active earning salary component',
            });
        }
    });
    if (issues.length === 0 && total !== acAmount) {
        issues.push({
            path: ['components'],
            code: 'components_total_mismatch',
            message: `Components total ${String(total)} but A/C amount is ${String(acAmount)}`,
        });
    }
    return issues;
}
//# sourceMappingURL=salary.js.map